/**
 * RoomTitleEditor — the room's one title, in the sidebar.
 *
 * A static heading unless the viewer owns the room. For the owner the heading
 * is a button: click to edit, Enter or blur saves, Escape cancels, an empty
 * value reverts. The draft lives here, so a `title` that changes while the
 * input is open (a socket title update) never replaces what the owner typed.
 */

import { KeyboardEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Heading } from '@/components/ui/Heading'
import { cn } from '@/lib/utils'

interface RoomTitleEditorProps {
  title: string | null | undefined
  /** True only when the viewer owns the room (`ChatRoomDto.viewerIsOwner`). */
  isEditable: boolean
  onRename?: (title: string) => unknown
  /** Test id of the heading, the same in both modes. */
  dataTestId?: string
  className?: string
}

export function RoomTitleEditor(props: RoomTitleEditorProps) {
  const {
    title,
    isEditable,
    onRename,
    dataTestId = 'chat-sidebar-group-title',
    className,
  } = props

  const { t } = useTranslation('app')
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const isSettledRef = useRef(false)

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isEditing])

  const displayTitle = title?.trim() || t('roomPane.newConversation')
  const canEdit = isEditable && Boolean(onRename)

  const onStartEdit = () => {
    isSettledRef.current = false
    setDraft(title?.trim() ?? '')
    setIsEditing(true)
  }

  const onCommit = () => {
    if (isSettledRef.current) {
      return
    }
    isSettledRef.current = true
    setIsEditing(false)
    const next = draft.trim()
    if (!next || next === title?.trim()) {
      return
    }
    void onRename?.(next)
  }

  const onCancel = () => {
    isSettledRef.current = true
    setIsEditing(false)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      onCommit()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      onCancel()
    }
  }

  const headingClassName = cn('truncate text-xl leading-[1.3] text-twin-chat-fg', className)

  if (!canEdit) {
    return (
      <Heading level={2} data-testid={dataTestId} className={headingClassName}>
        {displayTitle}
      </Heading>
    )
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        maxLength={200}
        aria-label={t('roomPane.renameTitle')}
        data-testid="chat-sidebar-title-input"
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={onCommit}
        className={cn(
          'w-full rounded-md border border-twin-chat-accent bg-transparent px-1.5 py-0.5 font-display text-xl leading-[1.3] text-twin-chat-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-twin-chat-accent',
          className,
        )}
      />
    )
  }

  return (
    <Heading level={2} data-testid={dataTestId} className={headingClassName}>
      <button
        type="button"
        onClick={onStartEdit}
        // The title stays the accessible name; the rename hint is its description.
        title={t('roomPane.renameTitle')}
        data-testid="chat-sidebar-title-edit"
        className="-mx-1.5 max-w-full truncate rounded-md px-1.5 text-left transition-colors hover:bg-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-twin-chat-accent dark:hover:bg-neutral-700"
      >
        {displayTitle}
      </button>
    </Heading>
  )
}
